<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
    />

    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <!-- Page header -->
          <div
            class="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ t("consult_edit_title") || "Edit Consultation" }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{
                  t("consult_edit_subtitle") || "Update consultation details"
                }}
              </p>
            </div>

            <div class="flex items-center gap-2">
              <button
                type="button"
                class="px-3 py-2 text-xs font-medium bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                @click="router.back()"
              >
                {{ t("back") || "Back" }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-slate-700 hover:bg-slate-700/90 disabled:opacity-60"
                :disabled="isSaving"
                @click="saveAll('draft')"
              >
                <i class="mdi mdi-content-save-edit-outline text-[16px]"></i>
                {{ t("consult_btn_save_draft") }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60"
                :disabled="isSaving"
                @click="saveAll"
              >
                <i class="mdi mdi-content-save-outline text-[16px]"></i>
                {{
                  isSaving
                    ? t("publish") || "Saving..."
                    : t("publish") || "publish"
                }}
              </button>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="isLoading" class="py-10 text-center text-slate-500">
            {{ t("loading") }}
          </div>

          <!-- Card wrapper -->
          <section
            v-else
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <!-- Inner card header -->
            <header
              class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
            >
              <div class="flex items-center gap-2">
                <i class="mdi mdi-square-edit-outline text-secondary"></i>
                <span>{{
                  t("consult_edit_title") || "Edit Consultation"
                }}</span>
              </div>

              <div class="flex items-center gap-2 text-xs text-slate-500">
                <span
                  class="px-2 py-1 font-mono border rounded-lg bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700"
                >
                  #{{ form.number || "—" }}
                </span>
                <span
                  class="px-2 py-1 border rounded-lg bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700"
                >
                  {{ form.date || "—" }}
                </span>
              </div>
            </header>

            <!-- BODY -->
            <div class="grid grid-cols-1 gap-4 p-5 lg:grid-cols-3">
              <!-- LEFT -->
              <aside class="space-y-4 lg:col-span-1">
                <!-- Consultation Info -->
                <section
                  class="p-4 border rounded-2xl bg-slate-50/70 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700"
                >
                  <div class="flex items-center gap-2 mb-3">
                    <i class="mdi mdi-information-outline text-secondary"></i>
                    <h3
                      class="text-xs font-semibold tracking-widest uppercase text-slate-600 dark:text-slate-300"
                    >
                      {{ t("consultation_info") || "Consultation Info" }}
                    </h3>
                  </div>

                  <div class="space-y-4">
                    <!-- Type -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consult_table_type") || "Type" }}
                      </label>
                      <SearchableSelect
                        v-model="typeModel"
                        :options="typeSelectOptions"
                        :lang="lang"
                        block
                        min-width="100%"
                        max-width="none"
                        menu-min-width="18rem"
                        menu-max-width="26rem"
                        :placeholder="
                          t('consult_field_type_placeholder') || 'Select type'
                        "
                        :search-placeholder="t('consult_filter_type_search')"
                        :empty-label="t('consult_filter_type_empty')"
                        :searchable="true"
                      />
                    </div>
                  </div>
                </section>

                <!-- Contact Methods -->
                <section
                  class="p-4 border rounded-2xl bg-slate-50/70 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700"
                >
                  <div class="flex items-center gap-2 mb-3">
                    <i class="mdi mdi-account-voice text-secondary"></i>
                    <h3
                      class="text-xs font-semibold tracking-widest uppercase text-slate-600 dark:text-slate-300"
                    >
                      {{ t("contact_method") || "Contact Method" }}
                    </h3>
                  </div>

                  <!-- Method cards -->
                  <div class="grid grid-cols-1 gap-3">
                    <!-- none -->
                    <button
                      type="button"
                      class="flex items-start gap-3 p-3 text-left transition border rounded-2xl"
                      :class="
                        form.method === 'none'
                          ? 'border-primary/50 bg-primary/5 ring-2 ring-primary/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-primary/40'
                      "
                      @click="selectMethod('none')"
                    >
                      <div
                        class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary"
                      >
                        <i class="mdi mdi-close-circle-outline text-[20px]"></i>
                      </div>
                      <div class="flex-1">
                        <div
                          class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {{ t("consult_method_none_title") || "None" }}
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400">
                          {{
                            t("consult_method_none_desc") ||
                            "No appointment details required."
                          }}
                        </div>
                      </div>
                      <i
                        v-if="form.method === 'none'"
                        class="mdi mdi-check-circle text-emerald-600 text-[18px] mt-1"
                      ></i>
                    </button>

                    <!-- chat -->
                    <button
                      type="button"
                      class="flex items-start gap-3 p-3 text-left transition border rounded-2xl"
                      :class="
                        form.method === 'chat'
                          ? 'border-primary/50 bg-primary/5 ring-2 ring-primary/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-primary/40'
                      "
                      @click="selectMethod('chat')"
                    >
                      <div
                        class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary"
                      >
                        <i class="mdi mdi-message-text-outline text-[20px]"></i>
                      </div>
                      <div class="flex-1">
                        <div
                          class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {{ t("consult_method_chat") || "Chat" }}
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400">
                          {{
                            t("consult_method_chat_desc") ||
                            "Instant chat at scheduled time."
                          }}
                        </div>
                      </div>
                      <i
                        v-if="form.method === 'chat'"
                        class="mdi mdi-check-circle text-emerald-600 text-[18px] mt-1"
                      ></i>
                    </button>

                    <!-- session -->
                    <button
                      type="button"
                      class="flex items-start gap-3 p-3 text-left transition border rounded-2xl"
                      :class="
                        form.method === 'session'
                          ? 'border-primary/50 bg-primary/5 ring-2 ring-primary/30'
                          : 'border-slate-200 dark:border-slate-700 hover:border-primary/40'
                      "
                      @click="selectMethod('session')"
                    >
                      <div
                        class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary"
                      >
                        <i class="mdi mdi-video-outline text-[20px]"></i>
                      </div>
                      <div class="flex-1">
                        <div
                          class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                        >
                          {{ t("consult_method_session") || "Session" }}
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400">
                          {{
                            t("consult_method_video_desc") ||
                            "Video call with meeting link."
                          }}
                        </div>
                      </div>
                      <i
                        v-if="form.method === 'session'"
                        class="mdi mdi-check-circle text-emerald-600 text-[18px] mt-1"
                      ></i>
                    </button>
                  </div>

                  <!-- Details panel -->
                  <div
                    v-if="form.method && form.method !== 'none'"
                    class="mt-4 overflow-hidden border rounded-2xl border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50"
                  >
                    <div
                      class="flex items-center gap-2 px-4 py-2 text-sm font-medium border-b border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-800/70"
                    >
                      <i
                        class="text-secondary"
                        :class="
                          form.method === 'session'
                            ? 'mdi mdi-video-outline'
                            : 'mdi mdi-message-text-outline'
                        "
                      ></i>
                      <span>
                        {{
                          form.method === "session"
                            ? t("consult_method_video_title") ||
                              "Session details"
                            : t("consult_method_chat_title") || "Chat details"
                        }}
                      </span>
                    </div>

                    <div class="px-4 py-4 space-y-4">
                      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <!-- Date -->
                        <div>
                          <label
                            class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                          >
                            {{ t("date") || "Date" }}
                          </label>

                          <div class="relative">
                            <span
                              class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                              :class="
                                lang === 'ar'
                                  ? 'right-0 rounded-r-lg'
                                  : 'left-0 rounded-l-lg'
                              "
                            >
                              <i
                                class="mdi mdi-calendar-blank-outline text-[18px]"
                              ></i>
                            </span>

                            <input
                              v-model="form.method_date"
                              type="date"
                              class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                              :class="[
                                lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3',
                                firstError(
                                  'oppointment_date',
                                  'method_date',
                                  'appointment_date'
                                )
                                  ? 'border-rose-500 ring-1 ring-rose-200'
                                  : 'border-slate-200 dark:border-slate-700',
                              ]"
                            />
                          </div>

                          <p
                            v-if="
                              firstError(
                                'oppointment_date',
                                'method_date',
                                'appointment_date'
                              )
                            "
                            class="mt-1 text-[11px] text-rose-600"
                          >
                            {{
                              firstError(
                                "oppointment_date",
                                "method_date",
                                "appointment_date"
                              )
                            }}
                          </p>
                        </div>

                        <!-- Time -->
                        <div>
                          <label
                            class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                          >
                            {{ t("time") || "Time" }}
                          </label>

                          <div class="relative">
                            <span
                              class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                              :class="
                                lang === 'ar'
                                  ? 'right-0 rounded-r-lg'
                                  : 'left-0 rounded-l-lg'
                              "
                            >
                              <i class="mdi mdi-clock-outline text-[18px]"></i>
                            </span>

                            <input
                              v-model="form.method_time"
                              type="time"
                              class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                              :class="[
                                lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3',
                                firstError(
                                  'oppointment_time',
                                  'method_time',
                                  'appointment_time'
                                )
                                  ? 'border-rose-500 ring-1 ring-rose-200'
                                  : 'border-slate-200 dark:border-slate-700',
                              ]"
                            />
                          </div>

                          <p
                            v-if="
                              firstError(
                                'oppointment_time',
                                'method_time',
                                'appointment_time'
                              )
                            "
                            class="mt-1 text-[11px] text-rose-600"
                          >
                            {{
                              firstError(
                                "oppointment_time",
                                "method_time",
                                "appointment_time"
                              )
                            }}
                          </p>
                        </div>
                      </div>

                      <!-- URL -->
                      <div v-if="form.method === 'session'">
                        <label
                          class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                        >
                          {{ t("session_link") || "Session link" }}
                        </label>

                        <div class="relative">
                          <span
                            class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                            :class="
                              lang === 'ar'
                                ? 'right-0 rounded-r-lg'
                                : 'left-0 rounded-l-lg'
                            "
                          >
                            <i class="mdi mdi-link-variant text-[18px]"></i>
                          </span>

                          <input
                            v-model="form.method_url"
                            type="url"
                            class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            :class="[
                              lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3',
                              firstError(
                                'oppointment_url',
                                'method_url',
                                'appointment_url'
                              )
                                ? 'border-rose-500 ring-1 ring-rose-200'
                                : 'border-slate-200 dark:border-slate-700',
                            ]"
                            placeholder="https://..."
                          />
                        </div>

                        <p
                          v-if="
                            firstError(
                              'oppointment_url',
                              'method_url',
                              'appointment_url'
                            )
                          "
                          class="mt-1 text-[11px] text-rose-600"
                        >
                          {{
                            firstError(
                              "oppointment_url",
                              "method_url",
                              "appointment_url"
                            )
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </aside>

              <!-- RIGHT -->
              <section class="space-y-4 lg:col-span-2">
                <!-- Main form -->
                <section
                  class="p-4 border rounded-2xl bg-white/70 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700"
                >
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <!-- Subject -->
                    <div class="md:col-span-2">
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consult_table_subject") || "Subject" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-9 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-lg'
                              : 'left-0 rounded-l-lg'
                          "
                        >
                          <i class="mdi mdi-form-textbox text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.subject"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                        />
                      </div>
                    </div>

                    <!-- Description -->
                    <div class="md:col-span-2">
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("description") || "Description" }}
                      </label>
                      <textarea
                        v-model="form.description"
                        rows="4"
                        class="w-full px-3 py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      ></textarea>
                    </div>
                  </div>
                </section>

                <!-- Attachments -->
                <section
                  class="overflow-hidden border rounded-2xl border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40"
                >
                  <div
                    class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                  >
                    <i class="mdi mdi-paperclip text-secondary"></i>
                    <span>
                      {{ t("attachments") || "Attachments" }}
                    </span>
                    <span class="text-xs text-slate-400">
                      ({{ attachments.length }})
                    </span>

                    <label
                      class="inline-flex items-center gap-2 px-3 py-1.5 ml-auto text-xs font-semibold text-white rounded-lg cursor-pointer bg-primary hover:bg-primary/90"
                    >
                      <i class="mdi mdi-plus"></i>
                      {{ t("add_attachment") || "Add" }}
                      <input
                        type="file"
                        class="hidden"
                        multiple
                        @change="onPickFiles"
                      />
                    </label>
                  </div>

                  <div class="px-5 py-4">
                    <div
                      v-if="attachments.length"
                      class="grid grid-cols-1 gap-3 md:grid-cols-2"
                    >
                      <div
                        v-for="file in attachments"
                        :key="file.id || file.tmpKey"
                        class="flex items-center justify-between gap-3 p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700"
                      >
                        <div class="flex items-center min-w-0 gap-3">
                          <div
                            class="flex items-center justify-center rounded-lg w-9 h-9 bg-primary/10 text-primary"
                          >
                            <i class="mdi mdi-file-outline text-[18px]"></i>
                          </div>
                          <div class="min-w-0">
                            <div
                              class="text-xs font-medium truncate text-slate-800 dark:text-slate-100"
                            >
                              {{ file.name }}
                            </div>
                            <div
                              class="text-[11px] text-slate-500 dark:text-slate-400"
                            >
                              {{ file.sizeLabel }}
                            </div>
                          </div>
                        </div>

                        <div class="flex items-center gap-2 shrink-0">
                          <a
                            v-if="file.url"
                            :href="file.url"
                            target="_blank"
                            class="px-2 py-1 text-[11px] rounded-lg bg-secondary/10 text-secondary hover:underline"
                          >
                            {{ t("view") || "View" }}
                          </a>

                          <button
                            type="button"
                            class="px-2 py-1 text-[11px] rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100"
                            @click="removeAttachment(file)"
                          >
                            {{ t("delete") || "Delete" }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="flex flex-col items-center justify-center gap-1 px-4 py-8 text-sm border-2 border-dashed rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40"
                    >
                      <i
                        class="text-2xl mdi mdi-cloud-upload-outline text-secondary"
                      ></i>
                      <div class="text-slate-700 dark:text-slate-200">
                        {{ t("no_attachments") || "No attachments yet." }}
                      </div>
                      <div class="text-xs text-slate-400">
                        {{
                          t("consult_attachments_hint") ||
                          "Click Add to upload files."
                        }}
                      </div>
                    </div>
                  </div>
                </section>
              </section>
            </div>

            <!-- Footer -->
            <footer
              class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
            >
              <p class="text-[11px] text-slate-400">
                {{ t("consult_required_note") || "" }}
              </p>

              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border rounded-lg border-slate-200 text-slate-600 bg-white/70 dark:bg-slate-900/70 dark:border-slate-700 dark:text-slate-200 disabled:opacity-60"
                  :disabled="isSaving"
                  @click="router.back()"
                >
                  {{ t("back") || "Back" }}
                </button>
                <button
                  type="button"
                  class="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg bg-slate-700 hover:bg-slate-700/90 disabled:opacity-60"
                  :disabled="isSaving"
                  @click="saveAll('draft')"
                >
                  <i class="mdi mdi-content-save-edit-outline text-[16px]"></i>
                  {{ t("consult_btn_save_draft") }}
                </button>
                <button
                  type="button"
                  class="inline-flex items-center justify-center px-5 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60"
                  :disabled="isSaving"
                  @click="saveAll"
                >
                  <i class="mdi mdi-content-save-outline text-[16px]"></i>
                  {{
                    isSaving
                      ? t("publish") || "publish..."
                      : t("publish") || "publish"
                  }}
                </button>
              </div>
            </footer>
          </section>
        </div>
        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import api from "@/services/api";
import Toast from "@/components/Toast.vue";

// ✅ ONLY ADD: store import
import { useFeedStore } from "@/stores/feedStore";
import SearchableSelect from "@/components/SearchableSelect.vue";

/* -------- lang same as your pages -------- */
const initialLang = (
  document.documentElement.lang ||
  localStorage.getItem("app_lang") ||
  "ar"
).toLowerCase();

const lang = ref(initialLang.startsWith("ar") ? "ar" : "en");
const _langObserver = ref(null);
const _langHandler = (e) => {
  const next = (e.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
};
window.addEventListener("app:lang", _langHandler, { passive: true });

_langObserver.value = new MutationObserver((muts) => {
  for (const m of muts) {
    if (m?.attributeName === "lang" || m?.attributeName === "dir") {
      const htmlLang = (
        document.documentElement.lang ||
        localStorage.getItem("app_lang") ||
        "ar"
      ).toLowerCase();
      const next = htmlLang.startsWith("en") ? "en" : "ar";
      if (next !== lang.value) lang.value = next;
    }
  }
});
_langObserver.value.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["lang", "dir"],
});

function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

// ترجمة رسائل الـ validation (server/client)
function trError(msg) {
  if (!msg) return "";

  const s = String(msg).toLowerCase();

  if (s.includes("required"))
    return t("validation_required") || t("required") || "Required";
  if (s.includes("invalid") || s.includes("format"))
    return t("validation_invalid") || "Invalid";
  if (s.includes("url")) return t("validation_url") || "Invalid URL";
  if (s.includes("date")) return t("validation_date") || "Invalid date";
  if (s.includes("time")) return t("validation_time") || "Invalid time";

  const translated = t(msg);
  return translated !== msg ? translated : msg;
}

// helper يرجع أول خطأ متوفر من عدة مفاتيح (client + server)
function firstError(...keys) {
  for (const k of keys) {
    if (errors[k]) return trError(errors[k]);
  }
  return "";
}

function selectMethod(key) {
  form.method = key;
  clearErrors();

  if (key === "none") {
    form.method_date = null;
    form.method_time = null;
    form.method_url = null;
    return;
  }

  if (key === "chat") {
    form.method_url = null;
    return;
  }
}

const toast = reactive({ show: false, type: "success", message: "" });

function showToast(message, type = "success") {
  toast.message = message;
  toast.type = type;
  toast.show = true;

  setTimeout(() => {
    toast.show = false;
  }, 2500);
}

/* -------- layout -------- */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");
function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

/* -------- router -------- */
const router = useRouter();
const route = useRoute();
const id = route.params.id;

const errors = reactive({});

function clearErrors() {
  Object.keys(errors).forEach((k) => delete errors[k]);
}

function setServerErrors(e) {
  clearErrors();
  const bag = e?.response?.data?.errors || {};
  Object.keys(bag).forEach((k) => {
    errors[k] = Array.isArray(bag[k]) ? bag[k][0] : String(bag[k]);
  });
}

function validateBeforeSave(mode = "open") {
  clearErrors();

  if (mode === "draft") return true;

  if (form.method === "chat" || form.method === "session") {
    if (!form.method_date) errors.oppointment_date = "validation_required";
    if (!form.method_time) errors.oppointment_time = "validation_required";
  }

  if (form.method === "session") {
    if (!form.method_url) errors.oppointment_url = "validation_required";
  }

  return Object.keys(errors).length === 0;
}

/* -------- options -------- */
const statusOptions = [
  { value: "under_study", label: "consult_status_under_study" },
  { value: "open", label: "consult_status_open" },
  { value: "in_progress", label: "consult_status_in_progress" },
  { value: "answered", label: "consult_status_answered" },
  { value: "closed", label: "consult_status_closed" },
];

// ✅ ONLY CHANGE: typeOptions now from store (dynamic)
const feedStore = useFeedStore();

const typeOptions = computed(() => {
  // support different naming
  const list =
    feedStore.consultationTypes ||
    feedStore.consultations_types ||
    feedStore.consultation_types ||
    [];

  return (Array.isArray(list) ? list : []).map((tp) => ({
    value: Number(tp.id),
    label:
      lang.value === "ar"
        ? tp?.name?.ar || tp?.name_ar || tp?.title?.ar || tp?.title_ar || ""
        : tp?.name?.en || tp?.name_en || tp?.title?.en || tp?.title_en || "",
  }));
});

const typeModel = computed({
  get: () => form.type ?? "",
  set: (value) => {
    form.type = value === "" || value == null ? null : Number(value);
  },
});

/* -------- form state -------- */
const isLoading = ref(false);
const isSaving = ref(false);

const form = reactive({
  number: "",
  date: "",
  subject: "",
  description: "",
  priority: "normal",

  status: "under_study",
  type: null,

  method: null,
  method_date: null,
  method_time: null,
  method_url: null,

  offer_before_tax: 0,
  tax_percent: 15,

  consultant_response: "",
});
const removedExistingAttachments = ref([]);

const attachments = ref([]);

const totalWithTax = computed(() => {
  const before = Number(form.offer_before_tax || 0);
  const taxP = Number(form.tax_percent || 0);
  return before + before * (taxP / 100);
});
const statusMapToId = {
  draft: 1,
  open: 2,
  in_progress: 3,
  answered: 4,
  closed: 5,
};

const statusMapById = {
  1: "draft",
  2: "open",
  3: "in_progress",
  4: "answered",
  5: "closed",
};

/* -------- helpers -------- */
function fileSizeLabel(bytes) {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return kb.toFixed(1) + " KB";
  return (kb / 1024).toFixed(1) + " MB";
}

/* -------- fetch details -------- */
async function fetchDetails() {
  isLoading.value = true;
  try {
    const res = await api.get(`/consultations/${id}`);
    const c = res.data?.data || res.data;

    form.number = c.consultation_number || c.number || "";
    form.date = c.date || "";
    form.subject = c.subject || "";
    form.description = c.description || "";
    form.priority = c.priority || "normal";

    form.status = statusMapById[c.status?.id] || "draft";

    // ✅ ensures the selected type is set by ID (works with dynamic options)
    form.type = typeof c.type === "object" ? c.type?.id : c.type ?? null;

    form.method = c.oppointment_method || c.contact_method || "none";
    form.method_date = c.oppointment_date || c.method_date || null;
    form.method_time = c.oppointment_time || c.method_time || null;
    form.method_url = c.oppointment_url || c.method_url || null;

    form.offer_before_tax = c.offer_before_tax ?? c.offer_price_before_tax ?? 0;
    form.tax_percent = c.tax_percent ?? 15;

    form.consultant_response = c.consultant_response || c.response || "";

    attachments.value = (c.attachments || []).map((a) => {
      if (typeof a === "string") {
        const url = a;
        const clean = url.split("?")[0];
        const name = decodeURIComponent(clean.split("/").pop() || "attachment");

        return {
          id: null,
          name,
          url,
          sizeLabel: "—",
        };
      }

      return {
        id: a.id ?? null,
        name: a.name || a.file_name || "attachment",
        url: a.url || a.path || a,
        sizeLabel: fileSizeLabel(a.size),
      };
    });
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
}

/* -------- pick files -------- */
function onPickFiles(e) {
  const files = Array.from(e.target.files || []);
  files.forEach((f) => {
    attachments.value.push({
      tmpKey: crypto.randomUUID(),
      file: f,
      name: f.name,
      url: null,
      sizeLabel: fileSizeLabel(f.size),
    });
  });
  e.target.value = "";
}

/* -------- remove attachment -------- */
async function removeAttachment(file) {
  if (file.tmpKey) {
    attachments.value = attachments.value.filter(
      (x) => x.tmpKey !== file.tmpKey
    );
    showToast(t("deleted_success") || "Deleted", "success");
    return;
  }

  if (file.url) removedExistingAttachments.value.push(file.url);
  attachments.value = attachments.value.filter((x) => x.url !== file.url);

  showToast(t("deleted_success") || "Deleted", "success");
}

/* -------- save blocks -------- */
async function saveAll(mode = "open") {
  if (!validateBeforeSave(mode)) {
    showToast(
      t("please_fix_errors") || "Please fix the highlighted errors",
      "error"
    );
    return;
  }

  const statusId = mode === "draft" ? statusMapToId.draft : statusMapToId.open;

  const existingUrls = attachments.value
    .filter((x) => x.url && !x.tmpKey)
    .map((x) => x.url);

  const newFiles = attachments.value
    .filter((x) => x.tmpKey && x.file)
    .map((x) => x.file);

  isSaving.value = true;
  try {
    const attachmentsAdd = await filesToPayload(newFiles);

    const payload = {
      subject: form.subject || "",
      description: form.description || "",
      priority: form.priority || "normal",

      type_id: form.type ?? null,
      status_id: statusId,

      oppointment_method: form.method || "none",
      oppointment_date: form.method_date || null,
      oppointment_time: form.method_time || null,
      oppointment_url: form.method_url || null,

      attachments_add: attachmentsAdd,
      existing_attachments: existingUrls,
      removed_existing_attachments: removedExistingAttachments.value,
    };

    console.log("PUT PAYLOAD:", payload);

    await api.put(`/consultations/${id}`, payload, {
      headers: { Accept: "application/json" },
    });

    await fetchDetails();

    removedExistingAttachments.value = [];

    if (mode === "draft") {
      showToast(t("consult_update_success") || "Saved successfully", "success");
      router.push({ name: "consultations.my", query: { toast: "updated" } });
      return;
    }

    showToast(t("consult_update_success") || "Saved successfully", "success");
    router.push({ name: "consultations.my", query: { toast: "updated" } });
  } catch (e) {
    console.error("SAVE ERROR:", e?.response || e);

    if (e?.response?.status === 422) {
      setServerErrors(e);
      showToast(
        t("please_fix_errors") || "Please fix the highlighted errors",
        "error"
      );
    } else {
      showToast(
        e?.response?.data?.message || e.message || "Something went wrong",
        "error"
      );
    }
  } finally {
    isSaving.value = false;
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function filesToPayload(files) {
  const out = [];
  for (const f of files) {
    const dataUrl = await fileToBase64(f);
    const base64 = String(dataUrl).includes("base64,")
      ? String(dataUrl).split("base64,")[1]
      : String(dataUrl);

    out.push({
      base64,
      name: f.name,
      type: f.type || "application/octet-stream",
      size: f.size || 0,
    });
  }
  return out;
}

// ✅ IMPORTANT: load store first, then fetchDetails so selected type appears
onMounted(async () => {
  try {
    // if your store supports it, this avoids refetching repeatedly
    if (!feedStore.loaded && !feedStore.loading) {
      await feedStore.fetchFeedOnce({ lang: lang.value });
    } else if (typeof feedStore.fetchFeedOnce === "function") {
      // fallback: still safe
      await feedStore.fetchFeedOnce({ lang: lang.value });
    }
  } catch (e) {
    // ignore feed errors, page still loads
  }

  await fetchDetails();
});

onBeforeUnmount(() => {
  if (_langObserver.value) _langObserver.value.disconnect();
  window.removeEventListener("app:lang", _langHandler);
});
</script>
